/**
 * Extração de Dados (migrada do Streamlit; equivalência em
 * backend/tests_paginas/test_extracao.py).
 *
 * Na URL: fonte, itens (códigos ou CNPJs, vírgula), campos (vírgula; vazio = padrão da
 * fonte) e período (vazio = últimos 5 anos). Como no legado, a extração só roda ao clicar
 * em "Extrair dados": os campos editam um rascunho, que vai para a URL no clique.
 */
import { Alert, Button, Group, Loader, MultiSelect, Text } from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { ErroApi, apiGet, montarUrl, type Envelope } from '../../api/cliente';
import { AvisosApi } from '../../components/AvisosApi';
import {
  BarraFiltros,
  CampoData,
  CampoOpcoes,
  GrupoFiltros,
  LARGURA,
} from '../../components/Filtros';
import { CabecalhoPagina, Pagina, Secao } from '../../components/Layout';
import { TabelaDados, type TabelaApi } from '../../components/TabelaDados';
import { useFiltrosUrl } from '../../lib/url-state';

const CAMINHO = '/utilitarios/extracao-de-dados';

type Fonte = 'indicadores' | 'fundos';
const CAMPO_PADRAO: Record<Fonte, string> = { indicadores: 'close', fundos: 'fund_nav' };

interface Catalogo {
  indicadores: string[];
  fundos: { cnpj: string; rotulo: string }[];
  campos: Record<Fonte, string[]>;
  padrao: { inicio: string; fim: string };
  max_itens: number;
}

interface DadosExtracao {
  linhas: number;
  colunas: number;
  tabela: TabelaApi;
}

interface Selecao {
  fonte: Fonte;
  itens: string[];
  campos: string[];
  inicio: string;
  fim: string;
}

const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Atalhos até hoje: N anos, YTD e Máx (30 anos). Mesma regra do catálogo de períodos
 * (backend/apps/core/periodos.py): N anos = o mesmo dia N anos antes (29/02 vira 28/02),
 * não 365 dias por ano como no legado.
 */
export function periodoAtalho(atalho: string, hoje = new Date()): { inicio: string; fim: string } {
  if (atalho === 'YTD') return { inicio: `${hoje.getFullYear()}-01-01`, fim: iso(hoje) };
  const anos = atalho === 'Máx' ? 30 : Number(atalho.replace('A', ''));
  const inicio = new Date(hoje.getFullYear() - anos, hoje.getMonth(), hoje.getDate());
  // 29/02 num ano não bissexto: o Date passa para 01/03; volta ao último dia de fevereiro.
  if (inicio.getMonth() !== hoje.getMonth()) inicio.setDate(0);
  return { inicio: iso(inicio), fim: iso(hoje) };
}

const ATALHOS = ['1A', '3A', '5A', '10A', 'YTD', 'Máx'];

function lista(texto: string) {
  return texto ? texto.split(',') : [];
}

function mensagem(erro: unknown) {
  return erro instanceof ErroApi ? erro.message : 'Não foi possível extrair os dados.';
}

/** Rascunho dos campos; recriado (key) quando a URL muda. */
function Formulario({
  inicial,
  catalogo,
  carregando,
  aoExtrair,
}: {
  inicial: Selecao;
  catalogo: Catalogo | undefined;
  carregando: boolean;
  aoExtrair: (s: Selecao) => void;
}) {
  const [s, setS] = useState(inicial);
  const mudar = (m: Partial<Selecao>) => setS((atual) => ({ ...atual, ...m }));
  const indicadores = s.fonte === 'indicadores';
  const opcoesItens = indicadores
    ? (catalogo?.indicadores ?? [])
    : (catalogo?.fundos ?? []).map((f) => ({ value: f.cnpj, label: f.rotulo }));
  const max = catalogo?.max_itens ?? 30;
  const invalida = s.inicio > s.fim;

  return (
    <BarraFiltros carregando={carregando}>
      <GrupoFiltros titulo="Fonte">
        <CampoOpcoes
          rotulo="Dados de"
          valor={s.fonte}
          aoMudar={(f) =>
            mudar({ fonte: f as Fonte, itens: [], campos: [CAMPO_PADRAO[f as Fonte]] })
          }
          opcoes={[
            { valor: 'indicadores', rotulo: 'Indicadores' },
            { valor: 'fundos', rotulo: 'Fundos' },
          ]}
        />
      </GrupoFiltros>
      <GrupoFiltros titulo="Período">
        <CampoData rotulo="Início" valor={s.inicio} aoMudar={(d) => mudar({ inicio: d })} />
        <CampoData rotulo="Fim" valor={s.fim} aoMudar={(d) => mudar({ fim: d })} />
        <Group gap={4} mt={25}>
          {ATALHOS.map((a) => (
            <Button
              key={a}
              size="compact-sm"
              variant="default"
              onClick={() => mudar(periodoAtalho(a))}
            >
              {a}
            </Button>
          ))}
        </Group>
      </GrupoFiltros>
      <GrupoFiltros titulo="Séries">
        <MultiSelect
          label={indicadores ? 'Códigos' : 'Fundos'}
          placeholder={indicadores ? 'Digite para buscar um código' : 'Digite para buscar um fundo'}
          description={`Até ${max}`}
          searchable
          clearable
          limit={100}
          maxValues={max}
          data={opcoesItens}
          value={s.itens}
          onChange={(itens) => mudar({ itens })}
          miw={LARGURA.selecao}
          w={indicadores ? 320 : 420}
        />
        <MultiSelect
          label="Campos"
          description="Um ou mais"
          data={catalogo?.campos[s.fonte] ?? []}
          value={s.campos}
          onChange={(campos) => mudar({ campos })}
          w={LARGURA.selecao}
        />
        <Button
          mt={42}
          color="dourado"
          disabled={!s.itens.length || !s.campos.length || invalida}
          onClick={() => aoExtrair(s)}
        >
          Extrair dados
        </Button>
      </GrupoFiltros>
      {invalida && (
        <Text size="sm" c="red" style={{ alignSelf: 'center' }}>
          A data de início não pode ser posterior à data de fim.
        </Text>
      )}
    </BarraFiltros>
  );
}

export function ExtracaoDados() {
  const [filtros, definir] = useFiltrosUrl({
    fonte: 'fundos',
    itens: '',
    campos: '',
    inicio: '',
    fim: '',
  });
  const fonte: Fonte = filtros.fonte === 'indicadores' ? 'indicadores' : 'fundos';
  const itens = lista(filtros.itens);
  const campos = filtros.campos ? lista(filtros.campos) : [CAMPO_PADRAO[fonte]];

  const catalogo = useQuery({
    queryKey: ['utilitarios.extracao.catalogo'],
    queryFn: ({ signal }) => apiGet<Envelope<Catalogo>>(`${CAMINHO}/catalogo`, {}, signal),
    staleTime: 60 * 60 * 1000,
  });
  const cat = catalogo.data?.data;

  const parametros = {
    fonte,
    itens,
    campos,
    inicio: filtros.inicio || undefined,
    fim: filtros.fim || undefined,
  };
  const extracao = useQuery({
    queryKey: ['utilitarios.extracao', parametros],
    queryFn: ({ signal }) => apiGet<Envelope<DadosExtracao>>(CAMINHO, parametros, signal),
    enabled: itens.length > 0,
    placeholderData: keepPreviousData,
  });
  const dados = extracao.data?.data;

  const inicial: Selecao = {
    fonte,
    itens,
    campos,
    inicio: filtros.inicio || cat?.padrao.inicio || '',
    fim: filtros.fim || cat?.padrao.fim || '',
  };
  const extrair = (s: Selecao) =>
    definir({
      fonte: s.fonte,
      itens: s.itens.join(','),
      campos: s.campos.join(',') === CAMPO_PADRAO[s.fonte] ? '' : s.campos.join(','),
      // Período padrão fica fora da URL (o link continua "últimos 5 anos").
      inicio: s.inicio === cat?.padrao.inicio ? '' : s.inicio,
      fim: s.fim === cat?.padrao.fim ? '' : s.fim,
    });

  return (
    <Pagina>
      <CabecalhoPagina
        titulo="Extração de Dados"
        descricao="Dados brutos de indicadores ou de fundos, por período, para consulta ou download em CSV."
        dadosAte={extracao.data?.meta.dados_ate}
        fonte="Banco Persevera (indicadores e fundos_cvm) · Fibery (catálogo de fundos)"
      />
      <Formulario
        key={`${JSON.stringify(inicial)}`}
        inicial={inicial}
        catalogo={cat}
        carregando={catalogo.isFetching || extracao.isFetching}
        aoExtrair={extrair}
      />
      <AvisosApi avisos={[catalogo.data?.meta.avisos, extracao.data?.meta.avisos]} />
      {catalogo.isError && <Alert color="red">{mensagem(catalogo.error)}</Alert>}
      {itens.length === 0 && (
        <Alert color="blue">
          Selecione {fonte === 'indicadores' ? 'códigos' : 'fundos'} e campos e clique em{' '}
          <b>Extrair dados</b>.
        </Alert>
      )}
      {extracao.isError && (
        <Alert
          color={
            extracao.error instanceof ErroApi && extracao.error.status === 404 ? 'yellow' : 'red'
          }
        >
          {mensagem(extracao.error)}
        </Alert>
      )}
      {itens.length > 0 && extracao.isPending && <Loader />}
      {dados && !extracao.isError && (
        <Secao
          titulo={fonte === 'indicadores' ? 'Indicadores · dados brutos' : 'Fundos · dados brutos'}
          legenda={`${dados.linhas.toLocaleString('pt-BR')} linhas × ${dados.colunas} colunas, em ordem crescente de data. O CSV leva os valores com precisão total.`}
          acoes={
            <Button
              component="a"
              color="dourado"
              variant="light"
              href={montarUrl(`${CAMINHO}/csv`, parametros)}
            >
              Download CSV
            </Button>
          }
        >
          <TabelaDados tabela={dados.tabela} altura={560} />
        </Secao>
      )}
    </Pagina>
  );
}
